// @ts-check
import { camelCaseObject, snakeCaseObject, getConfig } from '@edx/frontend-platform';
import { getAuthenticatedHttpClient } from '@edx/frontend-platform/auth';

export const getApiBaseUrl = () => getConfig().STUDIO_BASE_URL;
export const getApiLMSBaseUrl = () => getConfig().LMS_BASE_URL;
export const getApiDMBaseUrl = () => getConfig().MANAGER_BASE_URL;
export const getStudioHomeApiUrl = () => new URL('api/contentstore/v1/home', getApiBaseUrl()).href;
export const getRequestCourseCreatorUrl = () => new URL('request_course_creator', getApiBaseUrl()).href;
export const getCourseNotificationUrl = (url) => new URL(url, getApiBaseUrl()).href;

export const COMPANY_CACHED_DATA_ENDPOINTS = {
  allReflections: 'all-reflections',
  assessments: 'assessments',
  assessmentsV2: 'assessments-v2',
  completionBySkill: 'completion-by-skill',
  skillsDashboardAssignmentType: 'skills-dashboard-assignment-type',
  skillsDashboardAssignment: 'skills-dashboard-assignment',
  skillsDashboard: 'skills-dashboard',
  totalLearningOverview: 'total-learning-overview',
  unregisteredLearners: 'unregistered-learners',
};

export const PAGINATED_COMPANY_CACHED_DATA_ENDPOINTS = new Set([
  'allReflections',
  'assessments',
  'assessmentsV2',
  'completionBySkill',
  'skillsDashboardAssignment',
  'skillsDashboard',
  'unregisteredLearners',
]);

const ENTERPRISE_PAGE_SIZE = 50;

function isPaginatedEnterpriseResponse(data) {
  return (
    data
    && typeof data === 'object'
    && !Array.isArray(data)
    && Array.isArray(data.results)
    && Object.prototype.hasOwnProperty.call(data, 'next_page')
  );
}

async function getAllEnterprisePages(url, params) {
  const client = getAuthenticatedHttpClient();
  const results = [];
  let page = 1;

  do {
    // Requests must follow the server-provided next page in order.
    // eslint-disable-next-line no-await-in-loop
    const { data } = await client.get(url, {
      params: { ...params, page, page_size: ENTERPRISE_PAGE_SIZE },
    });

    // The pagination parameters are ignored until SBL-5973 is deployed.
    if (!isPaginatedEnterpriseResponse(data)) {
      return data;
    }

    results.push(...data.results);
    page = data.next_page;
  } while (page !== null);

  return results;
}

function buildCompanyCachedDataUrl(endpointPath, orgSlug, options = {}) {
  const { page, manifestOnly = false } = options;
  const url = new URL(`${getApiLMSBaseUrl()}/alias/company-data/nosql/${endpointPath}/`);
  url.searchParams.set('org-slug', orgSlug);
  if (manifestOnly) {
    url.searchParams.set('manifest', '1');
  }
  if (page != null) {
    url.searchParams.set('page', String(page));
  }
  return url.toString();
}

export function normalizeCompanyCachedDataResponse(responseData) {
  if (
    responseData
    && typeof responseData === 'object'
    && !Array.isArray(responseData)
    && 'pages' in responseData
    && 'total_pages' in responseData
  ) {
    return {
      manifest: responseData,
      data: null,
    };
  }

  if (
    responseData
    && typeof responseData === 'object'
    && !Array.isArray(responseData)
    && 'manifest' in responseData
    && 'data' in responseData
  ) {
    return {
      manifest: responseData.manifest ?? null,
      data: responseData.data ?? null,
    };
  }

  if (
    responseData
    && typeof responseData === 'object'
    && !Array.isArray(responseData)
    && 'data' in responseData
  ) {
    return {
      manifest: null,
      data: responseData.data ?? null,
    };
  }

  return {
    manifest: null,
    data: responseData ?? null,
  };
}

export async function getCompanyCachedDataManifest(companyName, endpoint) {
  const endpointPath = COMPANY_CACHED_DATA_ENDPOINTS[endpoint];
  const { data } = await getAuthenticatedHttpClient().get(
    buildCompanyCachedDataUrl(endpointPath, companyName, { manifestOnly: true }),
  );
  return normalizeCompanyCachedDataResponse(data).manifest;
}

export async function getCompanyCachedDataPage(companyName, endpoint, page = 1) {
  const endpointPath = COMPANY_CACHED_DATA_ENDPOINTS[endpoint];
  const { data } = await getAuthenticatedHttpClient().get(
    buildCompanyCachedDataUrl(endpointPath, companyName, page === 1 ? {} : { page }),
  );
  const normalized = normalizeCompanyCachedDataResponse(data);
  return {
    manifest: normalized.manifest,
    data: normalized.data,
  };
}

export async function getCompanyCachedDataAllPages(companyName, endpoint) {
  const endpointPath = COMPANY_CACHED_DATA_ENDPOINTS[endpoint];
  const { data } = await getAuthenticatedHttpClient().get(
    buildCompanyCachedDataUrl(endpointPath, companyName),
  );
  const normalized = normalizeCompanyCachedDataResponse(data);
  return {
    manifest: normalized.manifest,
    data: normalized.data,
  };
}

/**
 * Get's studio home data.
 * @returns {Promise<Object>}
 */
export async function getStudioHomeData() {
  const { data } = await getAuthenticatedHttpClient().get(getStudioHomeApiUrl());
  return camelCaseObject(data);
}

/** Get list of courses from the deprecated non-paginated API */
export async function getStudioHomeCourses(search) {
  const { data } = await getAuthenticatedHttpClient().get(`${getApiBaseUrl()}/api/contentstore/v1/home/courses${search}`);
  return camelCaseObject(data);
}
/**
 * Get's studio home courses.
 * @param {string} search - Query string parameters for filtering the courses.
 * @param {object} customParams - Additional custom parameters for the API request.
 * @returns {Promise<Object>} - A Promise that resolves to the response data containing the studio home courses.
 * Note: We are changing /api/contentstore/v1 to /api/contentstore/v2 due to upcoming breaking changes.
 * Features such as pagination, filtering, and ordering are better handled in the new version.
 * Please refer to this PR for further details: https://github.com/openedx/edx-platform/pull/34173
 */
export async function getStudioHomeCoursesV2(search, customParams) {
  const customParamsFormat = snakeCaseObject(customParams);
  const { data } = await getAuthenticatedHttpClient().get(`${getApiBaseUrl()}/api/contentstore/v2/home/courses${search}`, { params: customParamsFormat });
  return camelCaseObject(data);
}

export async function getStudioHomeLibraries() {
  const { data } = await getAuthenticatedHttpClient().get(`${getApiBaseUrl()}/api/contentstore/v1/home/libraries`);
  return camelCaseObject(data);
}

/**
 * Handle course notification requests.
 * @param {string} url
 * @returns {Promise<Object>}
*/
export async function handleCourseNotification(url) {
  const { data } = await getAuthenticatedHttpClient().delete(getCourseNotificationUrl(url));
  return camelCaseObject(data);
}

/**
 * Send user request to course creation access for studio home data.
 * @returns {Promise<Object>}
 */
export async function sendRequestForCourseCreator() {
  const { data } = await getAuthenticatedHttpClient().post(getRequestCourseCreatorUrl());
  return camelCaseObject(data);
}

/**
 * Company Management Portal
 */
export async function getCompanyManagementPortalGeneralData() {
  const { data } = await getAuthenticatedHttpClient().get(
    `${getApiLMSBaseUrl()}/api/ibl/dl_enterprise_app/enterprise`,
  );
  return data;
}

export async function getCompanyManagementPortalSpecificData(companyName) {
  const { data } = await getAuthenticatedHttpClient().get(
    `${getApiLMSBaseUrl()}/api/ibl/dl_enterprise_app/enterprise?company_name=${companyName}`,
  );
  return data;
}
/**
 * Get platforms data.
 * @param {string} key - The platform key to filter by (use '*' for all platforms)
 * @returns {Promise<Object>}
 */
export async function getPlatforms(key = '*') {
  const { data } = await getAuthenticatedHttpClient().get(
    `${getApiBaseUrl()}/api/sbl/platforms`,
    { params: { key } },
  );
  return data;
}

/**
 * Create a new company
 * @param {Object} companyData - The company data to create
 * @returns {Promise<Object>}
 */
export async function createCompany(companyData) {
  const { data } = await getAuthenticatedHttpClient().post(
    `${getApiLMSBaseUrl()}/api/ibl/dl_enterprise_app/enterprise`,
    companyData,
  );
  return data;
}

/**
 * Create a new platform.
 * @param {Object} platformData - The platform data to create
 * @param {string} platformData.key - The unique platform key
 * @param {string} platformData.org - The platform organization
 * @param {string} platformData.name - The platform name
 * @returns {Promise<Object>}
 */
export async function createPlatform(platformData) {
  const { data } = await getAuthenticatedHttpClient().post(
    `${getApiBaseUrl()}/api/sbl/platforms`,
    platformData,
  );
  return data;
}

/**
 * Inactivity Email Configuration API
 */

/**
 * Get inactivity email configuration for a specific organization.
 * Returns org-specific config from S3 if exists, otherwise returns default template.
 * @param {string} orgSlug - The organization slug
 * @param {string} orgName - The organization name
 * @returns {Promise<Object>} Organization configuration
 */
export async function getInactivityEmailConfig(orgSlug, orgName) {
  const apiBase = getConfig().MANAGER_BASE_URL || getConfig().LMS_BASE_URL;
  const { data } = await getAuthenticatedHttpClient().get(
    `${apiBase}/alias/inactivity-emails`,
    { params: { org_slug: orgSlug, org_name: orgName } },
  );
  return data;
}

/**
 * Update complete inactivity email configuration (PUT whole config to S3)
 * @param {Object} config - Complete inactivity email configuration.
 * @returns {Promise<Object>}
 */
export async function updateInactivityEmailConfig(config) {
  const apiBase = getConfig().MANAGER_BASE_URL || getConfig().LMS_BASE_URL;
  const { data } = await getAuthenticatedHttpClient().put(
    `${apiBase}/alias/inactivity-emails`,
    config,
  );
  return data;
}

/**
 * Create company catalog for license management
 * @param {Object} catalogData - The catalog data to create
 * @returns {Promise<Object>}
 */
export async function createCompanyCatalog(catalogData) {
  const { data } = await getAuthenticatedHttpClient().post(
    `${getApiLMSBaseUrl()}/api/ibl/dl_enterprise_app/enterprise/company-catalog/v2`,
    catalogData,
  );
  return data;
}

/**
 * Update company friendly name
 * @param {string} companyName - The company code
 * @param {string} friendlyName - The new friendly name
 * @returns {Promise<Object>}
 */
export async function updateCompanyFriendlyName(companyName, friendlyName) {
  const { data } = await getAuthenticatedHttpClient().post(
    `${getApiLMSBaseUrl()}/api/ibl/dl_enterprise_app/enterprise/update-friendly-name`,
    { company_name: companyName, new_friendly_name: friendlyName },
  );
  return data;
}

/**
 * Update company size
 * @param {string} companyName - The company code
 * @param {string} size - The new company size
 * @returns {Promise<Object>}
 */
export async function updateCompanySize(companyName, size) {
  const { data } = await getAuthenticatedHttpClient().post(
    `${getApiLMSBaseUrl()}/api/ibl/dl_enterprise_app/enterprise/update-size`,
    { company_name: companyName, size },
  );
  return data;
}

/**
 * Update company website
 * @param {string} companyName - The company code
 * @param {string} website - The new company website
 * @returns {Promise<Object>}
 */
export async function updateCompanyWebsite(companyName, website) {
  const { data } = await getAuthenticatedHttpClient().post(
    `${getApiLMSBaseUrl()}/api/ibl/dl_enterprise_app/enterprise/update-website`,
    { company_name: companyName, website },
  );
  return data;
}

/**
 * Update course authoring organization (edx_course_org)
 * @param {string} companyName - The company code
 * @param {string} edxCourseOrg - The course authoring organization
 * @returns {Promise<Object>}
 */
export async function updateCourseAuthoringOrg(companyName, edxCourseOrg) {
  const { data } = await getAuthenticatedHttpClient().patch(
    `${getApiLMSBaseUrl()}/api/ibl/dl_enterprise_app/enterprise/company-catalog/company`,
    {
      company_name: companyName,
      company: {
        edx_course_org: edxCourseOrg,
      },
    },
  );
  return data;
}

/**
 * Update skill select configuration
 * @param {string} companyName - The company code
 * @param {Object} skillSelectConfig - Skill select configuration
 * @returns {Promise<Object>}
 */
export async function updateSkillSelect(companyName, skillSelectConfig) {
  const { data } = await getAuthenticatedHttpClient().patch(
    `${getApiLMSBaseUrl()}/api/ibl/dl_enterprise_app/enterprise/company-catalog/company`,
    {
      company_name: companyName,
      company: {
        skill_select: skillSelectConfig,
      },
    },
  );
  return data;
}

/**
 * Update catalog options
 * @param {string} companyName - The company code
 * @param {Object} optionsConfig - Catalog options configuration
 * @returns {Promise<Object>}
 */
export async function updateCatalogOptions(companyName, optionsConfig) {
  const { data } = await getAuthenticatedHttpClient().patch(
    `${getApiLMSBaseUrl()}/api/ibl/dl_enterprise_app/enterprise/company-catalog/company`,
    {
      company_name: companyName,
      company: {
        options: optionsConfig,
      },
    },
  );
  return data;
}

/**
 * Add custom CSV fields
 * @param {string} companyName - The company code
 * @param {string[]} csvFields - The CSV fields to add
 * @returns {Promise<Object>}
 */
export async function addCustomCsvFields(companyName, csvFields) {
  const { data } = await getAuthenticatedHttpClient().post(
    `${getApiLMSBaseUrl()}/api/ibl/dl_enterprise_app/enterprise/add-custom-csv`,
    { company_name: companyName, csv: csvFields },
  );
  return data;
}

/**
 * Remove custom CSV fields
 * @param {string} companyName - The company code
 * @param {string[]} csvFields - The CSV fields to remove
 * @returns {Promise<Object>}
 */
export async function removeCustomCsvFields(companyName, csvFields) {
  const { data } = await getAuthenticatedHttpClient().post(
    `${getApiLMSBaseUrl()}/api/ibl/dl_enterprise_app/enterprise/remove-custom-csv`,
    { company_name: companyName, csv: csvFields },
  );
  return data;
}

/**
 * Add custom admin portal features
 * @param {string} companyName - The company code
 * @param {string[]} features - The features to add
 * @returns {Promise<Object>}
 */
export async function addCustomAdminPortalFeatures(companyName, features) {
  const { data } = await getAuthenticatedHttpClient().put(
    `${getApiLMSBaseUrl()}/api/ibl/dl_enterprise_app/enterprise/add-custom-admin-portal`,
    { company_name: companyName, custom_admin_portal: features },
  );
  return data;
}

/**
 * Remove custom admin portal features
 * @param {string} companyName - The company code
 * @param {string[]} features - The features to remove
 * @returns {Promise<Object>}
 */
export async function removeCustomAdminPortalFeatures(companyName, features) {
  const { data } = await getAuthenticatedHttpClient().delete(
    `${getApiLMSBaseUrl()}/api/ibl/dl_enterprise_app/enterprise/remove-custom-admin-portal`,
    { data: { company_name: companyName, custom_admin_portal: features } },
  );
  return data;
}

/**
 * Register bulk users (for adding new admins)
 * @param {Object} employeeData - The employee registration data
 * @returns {Promise<Object>}
 */
export async function registerBulkUsers(employeeData) {
  const { data } = await getAuthenticatedHttpClient().post(
    `${getApiLMSBaseUrl()}/sbl/register-bulk-users`,
    employeeData,
  );
  return data;
}

/**
 * Register learners with legacy bundle licenses
 * @param {string} companyName - The company code/slug
 * @param {Array} learners - Array of learner objects with first_name, last_name, email, and license array
 * @returns {Promise<Object>}
 */
export async function registerLearnersWithLegacyLicenses(companyName, learners) {
  const { data } = await getAuthenticatedHttpClient().post(
    `${getApiLMSBaseUrl()}/api/ibl/dl_enterprise_app/enterprise/bulk-register-learners`,
    { company_name: companyName, learners },
  );
  return data;
}

/**
 * Update an individual learner's enterprise details (job title, etc.)
 * @param {Object} payload - The learner update payload
 * @returns {Promise<Object>}
 */
export async function updateEnterpriseLearner(payload) {
  const { data } = await getAuthenticatedHttpClient().post(
    `${getApiLMSBaseUrl()}/alias/enterprise/manage-learner-membership`,
    payload,
  );
  return data;
}

/**
 * Remove an individual learner from an enterprise
 * @param {string} companyName - The company code
 * @param {string} username - The username to remove
 * @returns {Promise<Object>}
 */
export async function removeEnterpriseLearner(companyName, username) {
  const { data } = await getAuthenticatedHttpClient().delete(
    `${getApiLMSBaseUrl()}/alias/enterprise/manage-learner-membership`,
    { data: { company_name: companyName, username } },
  );
  return data;
}

/**
 * Add a user as an admin for a company
 * @param {string} companyName - The company code
 * @param {string} username - The username (or email) to add as admin
 * @returns {Promise<Object>}
 */
export async function addCompanyAdmins(companyName, username) {
  const { data } = await getAuthenticatedHttpClient().post(
    `${getApiLMSBaseUrl()}/alias/enterprise/manage-admin-membership`,
    { company_name: companyName, username },
  );
  return data;
}

/**
 * Remove a user's admin role from a company
 * @param {string} companyName - The company code
 * @param {string} username - The username (or email) to remove as admin
 * @returns {Promise<Object>}
 */
export async function removeCompanyAdmins(companyName, username) {
  const { data } = await getAuthenticatedHttpClient().delete(
    `${getApiLMSBaseUrl()}/alias/enterprise/manage-admin-membership`,
    { data: { company_name: companyName, username } },
  );
  return data;
}

/**
 * Update primary admin
 * @param {string} companyName - The company code
 * @param {string} username - The username (or email) of the new primary admin
 * @returns {Promise<Object>}
 */
export async function updatePrimaryAdmin(companyName, username) {
  const { data } = await getAuthenticatedHttpClient().post(
    `${getApiLMSBaseUrl()}/alias/enterprise/manage-primary-admin`,
    { company_name: companyName, username },
  );
  return data;
}

/**
 * Company Licenses Management APIs
 */

/**
 * Get company catalog (current catalog system)
 * @param {string} companyName - The company code
 * @returns {Promise<Object>}
 */
export async function getCompanyCatalog(companyName) {
  const { data } = await getAuthenticatedHttpClient().get(
    `${getApiLMSBaseUrl()}/api/ibl/dl_enterprise_app/enterprise/company-catalog/v2?company_name=${companyName}`,
  );
  return data;
}

/**
 * Add licenses (Legacy system)
 * @param {string} companyName - The company code
 * @param {Array} bundleList - Array of license bundles to add
 * @returns {Promise<Object>}
 */
export async function addLegacyLicenses(companyName, bundleList) {
  const { data } = await getAuthenticatedHttpClient().post(
    `${getApiLMSBaseUrl()}/api/ibl/dl_enterprise_app/enterprise/add-licenses`,
    { company_name: companyName, bundle_list: bundleList },
  );
  return data;
}

/**
 * Remove licenses (Legacy system)
 * @param {string} companyName - The company code
 * @param {Array} licenseNames - Array of license names to remove
 * @returns {Promise<Object>}
 */
export async function removeLegacyLicenses(companyName, licenseNames) {
  const { data } = await getAuthenticatedHttpClient().post(
    `${getApiLMSBaseUrl()}/api/ibl/dl_enterprise_app/enterprise/remove-licenses`,
    { company_name: companyName, bundle_list: licenseNames },
  );
  return data;
}

/**
 * Update company catalog (Current catalog system)
 * @param {string} companyName - The company code
 * @param {Array} catalog - Array of catalog items
 * @returns {Promise<Object>}
 */
export async function updateCompanyCatalog(companyName, catalog) {
  const { data } = await getAuthenticatedHttpClient().put(
    `${getApiLMSBaseUrl()}/api/ibl/dl_enterprise_app/enterprise/company-catalog/v2`,
    { company_name: companyName, catalog },
  );
  return data;
}

/**
 * Patch company catalog (for updating quantities)
 * @param {string} companyName - The company code
 * @param {Object} catalogList - Object with course IDs and their allocation data
 * @returns {Promise<Object>}
 */
export async function patchCompanyCatalog(companyName, catalogList) {
  const { data } = await getAuthenticatedHttpClient().patch(
    `${getApiLMSBaseUrl()}/api/ibl/dl_enterprise_app/enterprise/company-catalog/v2/catalog`,
    { company_name: companyName, catalog: catalogList },
  );
  return data;
}

/**
 * Delete company catalog items (remove courses from catalog)
 * @param {string} companyName - The company code
 * @param {Array} pkList - Array of primary keys to remove from catalog
 * @returns {Promise<Object>}
 */
export async function deleteCompanyCatalogItems(companyName, pkList) {
  const { data } = await getAuthenticatedHttpClient().delete(
    `${getApiLMSBaseUrl()}/api/ibl/dl_enterprise_app/enterprise/company-catalog/v2/catalog`,
    {
      data: { company_name: companyName, catalog: pkList },
    },
  );
  return data;
}

/**
 * Add company catalog items (add courses to catalog)
 * @param {string} companyName - The company code
 * @param {Object} catalogList - Object with course IDs and their allocation data
 * @returns {Promise<Object>}
 */
export async function addCompanyCatalogItems(companyName, catalogList) {
  const { data } = await getAuthenticatedHttpClient().patch(
    `${getApiLMSBaseUrl()}/api/ibl/dl_enterprise_app/enterprise/company-catalog/v2/catalog`,
    { company_name: companyName, catalog: catalogList },
  );
  return data;
}

/**
 * Update an existing platform.
 * @param {Object} platformData - The platform data to update
 * @param {string} platformData.key - The platform key
 * @param {Object} platformData.updates - The fields to update
 * @returns {Promise<Object>}
 */
export async function updatePlatform(platformData) {
  const { data } = await getAuthenticatedHttpClient().patch(
    `${getApiBaseUrl()}/api/sbl/platforms`,
    platformData,
  );
  return data;
}

/**
 * Get programs by platform key.
 * @param {string} platformKey - The platform key
 * @returns {Promise<Array>}
 */
export async function getPrograms(platformKey) {
  const { data } = await getAuthenticatedHttpClient().get(
    `${getApiBaseUrl()}/api/ibl/catalog/program/manage`,
    { params: { platform_key: platformKey } },
  );
  return Array.isArray(data) ? data : [];
}

/**
 * Create a new program.
 * @param {Object} programData - The program data to create
 * @param {string} programData.name - The program name
 * @param {string} programData.platformKey ||  @param {string} programData.platform_key - The platform key
 * @param {Array} [programData.courseList] - Optional list of course IDs
 * @param {string} [programData.description] - Optional description
 * @param {Array} [programData.tags] - Optional tags
 * @param {boolean} [programData.autoEnroll] - Optional auto-enrollment flag
 * @param {boolean} [programData.autoUnenroll] - Optional auto-unenrollment flag
 * @returns {Promise<Object>}
 */
export async function createProgram(programData) {
  const payload = { ...programData, platformKey: programData.platformKey || programData?.platform_key };
  delete payload.platformKey;
  const { data } = await getAuthenticatedHttpClient().post(
    `${getApiBaseUrl()}/api/ibl/catalog/program/manage`,
    payload,
  );
  return data;
}

/**
 * Update an existing program.
 * @param {Object} programData - The program data to update
 * @param {string} programData.itemId - The program item ID
 * @param {string} programData.name - The program name
 * @param {string} programData.platformKey ||  @param {string} programData.platform_key - The platform key
 * @param {Array} [programData.courseList] - Optional list of course IDs
 * @param {string} [programData.description] - Optional description
 * @param {Array} [programData.tags] - Optional tags
 * @param {boolean} [programData.autoEnroll] - Optional auto-enrollment flag
 * @param {boolean} [programData.autoUnenroll] - Optional auto-unenrollment flag
 * @returns {Promise<Object>}
 */
export async function updateProgram(programData) {
  const payload = { ...programData, platformKey: programData?.platformKey || programData?.platform_key };
  delete payload.platformKey;
  const { data } = await getAuthenticatedHttpClient().post(
    `${getApiBaseUrl()}/api/ibl/catalog/program/manage`,
    payload,
  );
  return data;
}

/**
 * Delete an existing program.
 * @param {string} platformKey - The platform key
 * @param {string} itemId - The program item ID to delete
 * @returns {Promise<Object>}
 */
export async function deleteProgram(platformKey, itemId) {
  const { data } = await getAuthenticatedHttpClient().delete(
    `${getApiBaseUrl()}/api/ibl/catalog/program/manage`,
    { params: { platform_key: platformKey, item_id: itemId } },
  );
  return data;
}

/**
 * Learner Enrollment APIs
 */

/**
 * Bulk enroll learners in catalog courses
 * @param {string} username - The username to enroll
 * @param {Array<string>} courses - Array of course IDs to enroll in
 * @returns {Promise<Object>}
 */
export async function bulkEnrollCourses(username, courses) {
  const { data } = await getAuthenticatedHttpClient().post(
    `${getApiLMSBaseUrl()}/api/ibl/enrollment/enroll/bulk`,
    { username, courses },
  );
  return data;
}

/**
 * Enroll learner in a pathway/program
 * @param {number} userId - The user's Django ID
 * @param {string} itemId - The pathway/program item ID
 * @param {string} org - The organization
 * @returns {Promise<Object>}
 */
export async function enrollPathway(userId, itemId, org) {
  const { data } = await getAuthenticatedHttpClient().post(
    `${getApiBaseUrl()}/api/ibl/catalog/program/enroll`,
    { user_id: userId, item_id: itemId, org },
  );
  return data;
}

/**
 * Get user profile by email (returns username, not Django ID)
 * @param {string} email - User email
 * @returns {Promise<Object>}
 */
export async function getUserProfileByEmail(email) {
  const { data } = await getAuthenticatedHttpClient().get(
    `${getApiLMSBaseUrl()}/ibl/user-profile`,
    { params: { email } },
  );
  return data;
}

/**
 * Get user details by username (returns Django ID)
 * @param {string} username - Username
 * @returns {Promise<Object>}
 */
export async function getUserDetailsByUsername(username) {
  const { data } = await getAuthenticatedHttpClient().get(
    `${getApiLMSBaseUrl()}/api/user/v1/accounts/${username}`,
  );
  return data;
}

/**
 * Get user details by email or username (returns both username and Django ID)
 * If email is provided, first looks up username, then gets Django ID
 * @param {string} identifier - Email or username
 * @returns {Promise<Object>} - Returns {username, id} and other user details
 */
export async function getUserDetails(identifier) {
  const isEmail = identifier.includes('@');

  if (isEmail) {
    // First get username from email
    const profileData = await getUserProfileByEmail(identifier);
    const username = profileData.username || identifier;

    // Then get full details including Django ID using username
    const userDetails = await getUserDetailsByUsername(username);
    return userDetails;
  }

  // If it's already a username, just get the details
  return getUserDetailsByUsername(identifier);
}

/**
 * Get complete user profile by email or username (for editing)
 * @param {string} identifier - Email or username
 * @returns {Promise<Object>} - Returns complete user profile with B2B data
 */
export async function getUserProfile(identifier) {
  const isEmail = identifier.includes('@');
  const params = isEmail ? { email: identifier } : { username: identifier };

  // Also get employee records from dm
  const { data } = await getAuthenticatedHttpClient().get(
    `${getApiLMSBaseUrl()}/ibl/user-profile`,
    { params },
  );
  const employeeData = await getAllEnterprisePages(
    `${getApiLMSBaseUrl()}/alias/enterprise/membership`,
    params,
  );
  data.employeeRecords = employeeData;
  return data;
}

/**
 * Update complete user profile (strips null values before sending)
 * @param {Object} userProfile - Complete user profile object
 * @returns {Promise<Object>}
 */
export async function updateUserProfileComplete(userProfile) {
  // Strip null values from the profile before sending
  const stripNullValues = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(stripNullValues).filter(item => item !== null);
    }
    if (obj !== null && typeof obj === 'object') {
      return Object.entries(obj)
        .filter(([, v]) => v !== null)
        .reduce((acc, [k, v]) => ({ ...acc, [k]: stripNullValues(v) }), {});
    }
    return obj;
  };

  // Create a copy to prevent mutating the original
  const profileToUpdate = { ...userProfile };
  // Do not send B2B or employeeRecords to the profile API as we now use direct endpoints
  delete profileToUpdate.b2b;
  delete profileToUpdate.employeeRecords;

  const cleanedProfile = stripNullValues(profileToUpdate);

  const { data } = await getAuthenticatedHttpClient().put(
    `${getApiLMSBaseUrl()}/ibl/user-profile`,
    cleanedProfile,
  );
  return data;
}

/**
 * Trigger admin portal cache refresh
 * External API - uses fetch() instead of getAuthenticatedHttpClient()
 * @param {string} companyName - The company name or '*' for all organizations
 * @returns {Promise<Object>}
 */
export async function triggerAdminPortalCacheRefresh(companyName) {
  const apiUrl = getConfig().API_GATEWAY_CACHED_DATA_REFRESH;
  const apiKey = getConfig().API_GATEWAY_XAPIKEY;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Api-Key': apiKey,
    },
    body: JSON.stringify({ b2b_org: companyName }),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const data = await response.json();
  return data;
}

/**
 * Run course aggregation
 * External API - uses fetch() with 2-second timeout
 * No auth headers required
 * Success criteria: ANY HTTP response (the POST reached the server)
 * Failure: Only true network errors (connection refused, DNS failure, etc.)
 * @returns {Promise<Object>}
 */
/**
 * Get the status of a bulk-register-users task.
 * @param {string} taskId - The task ID returned by POST /sbl/register-bulk-users
 * @returns {Promise<Object>} - { status, processed, total, percent, errors }
 */
export async function getBulkRegisterTaskStatus(taskId) {
  const { data } = await getAuthenticatedHttpClient().get(
    `${getApiLMSBaseUrl()}/sbl/register-bulk-users/status/${taskId}/`,
  );
  return data;
}

export async function runCourseAggregation() {
  // SBL-6018: retargeted from a raw fetch() directly against
  // MANAGER_BASE_URL (unauthenticated, bypassing the sanctioned
  // MFE -> edx-plugin -> Data Manager proxy pattern) to the authenticated
  // LMS proxy route, matching every other Data Manager call in this file.

  // Set 2-second timeout - if it doesn't respond, we assume it's pending (success)
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 2000);

  try {
    const response = await getAuthenticatedHttpClient().post(
      `${getApiLMSBaseUrl()}/api/ibl/course_aggregation_api/courses`,
      undefined,
      {
        signal: controller.signal,
      },
    );
    clearTimeout(timeoutId);

    // If we got ANY response from the server, the POST was successfully received
    // This includes 200, 202, 404, 500, 504, etc.
    return {
      success: true,
      statusCode: response.status,
      statusText: response.statusText,
    };
  } catch (error) {
    clearTimeout(timeoutId);

    // 2 seconds passed (AbortError) - request is pending, consider success
    if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
      return { success: true, timedOut: true, pending: true };
    }

    // If the server responded (even with an error status), axios attaches
    // response — treat this the same as the old fetch()-based "any response
    // received" success path.
    if (error && typeof error === 'object' && error.response) {
      return {
        success: true,
        statusCode: error.response.status,
        statusText: error.response.statusText,
      };
    }

    // Only throw for true network errors (connection refused, DNS failure, etc.)
    throw error;
  }
}

export async function getCompanyEmployees(companyName) {
  return getAllEnterprisePages(
    `${getApiLMSBaseUrl()}/alias/enterprise/employees`,
    { org_slug: companyName, sort: 'name', order: 'asc' },
  );
}

export async function getCompanyEmployeePage(companyName, page = 1, pageSize = ENTERPRISE_PAGE_SIZE) {
  const { data } = await getAuthenticatedHttpClient().get(
    `${getApiLMSBaseUrl()}/alias/enterprise/employees`,
    {
      params: {
        org_slug: companyName,
        page,
        page_size: pageSize,
        sort: 'name',
        order: 'asc',
      },
    },
  );

  if (!isPaginatedEnterpriseResponse(data)) {
    const results = Array.isArray(data) ? data : [];
    return { count: results.length, nextPage: null, results };
  }

  return {
    count: Number(data.count) || 0,
    nextPage: data.next_page,
    results: data.results,
  };
}

export async function getEnterpriseMemberships(email) {
  try {
    const { data } = await getAuthenticatedHttpClient().get(
      `${getApiLMSBaseUrl()}/alias/enterprise/membership`,
      { params: { email } },
    );
    return Array.isArray(data) ? data : (data?.memberships || data?.results || []);
  } catch (error) {
    if (error?.response?.data?.error === 'User not found') {
      return [];
    }
    throw error;
  }
}

/**
 * Fetch enrollment enrichment (courses, certificate, grade, progress) for a batch of learners.
 * Backed by `v1/dashboard/enrollments/bulk/` (ibl-catalog-dashboard-api). Keep the `learners`
 * array small (~25) — larger batches are noticeably slower (measured ~4-6s for 250 learners
 * with many course enrollments each).
 * @param {string} orgSlug - The org slug to scope the request to
 * @param {Array<{username: string} | {user_id: number} | {email: string}>} learners
 *   Learner identifiers
 * @param {Object} [options]
 * @param {string[]} [options.include] - Optional enrollment fields to enrich
 * @param {string} [options.scope] - Scope filter, e.g. 'org_catalog'
 * @returns {Promise<Object>} The raw bulk enrollments response ({ org_slug, scope, include, count, results })
 */
export async function getBulkLearnerEnrollments(orgSlug, learners, options = {}) {
  const { include = ['certificate', 'grade', 'last_active'], scope = 'org_catalog' } = options;
  const { data } = await getAuthenticatedHttpClient().post(
    `${getApiLMSBaseUrl()}/api/ibl/v1/dashboard/enrollments/bulk/`,
    {
      org_slug: orgSlug,
      scope,
      include,
      learners,
    },
  );

  return data;
}

export async function getCompanyCachedData(companyName) {
  const endpointKeys = Object.keys(COMPANY_CACHED_DATA_ENDPOINTS);
  const results = await Promise.all(endpointKeys.map(async (endpoint) => {
    const response = await getCompanyCachedDataAllPages(companyName, endpoint);
    return [endpoint, response.data ?? []];
  }));

  return Object.fromEntries(results);
}
